<template>
  <v-container
    fluid
    class="my-ranches"
    style="height: 100%; background-color: #ffffff"
  >
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="8">
        <h1 class="text-h5 mb-1">My Ranches</h1>
        <p class="mb-0 text-body-2">
          Manage your ranches: create new ones, see those shared with you, load
          a ranch into the application, or remove ranches you no longer need.
        </p>
      </v-col>
      <v-col cols="12" md="4" class="summary text-md-right mt-4 mt-md-0">
        <div class="text-caption">
          <span class="font-weight-medium">{{ myRanches.length }}</span>
          of your ranches
        </div>
        <div class="text-caption">
          <span class="font-weight-medium">{{ sharedRanches.length }}</span>
          shared with you
        </div>
        <div class="mt-2">
          <v-btn
            color="grey"
            outlined
            class="mr-2"
            @click="refreshRanchList"
            :loading="refreshing"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
          <v-btn color="primary" @click="openCreateDialog">
            <v-icon left>mdi-plus</v-icon>
            Create Ranch
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <!-- Loaded ranch details panel -->
      <v-col v-if="activeRanch" cols="12" md="4">
        <v-card class="pa-4" outlined>
          <v-card-title class="text-subtitle-1 pb-1"
            >Loaded Ranch Details</v-card-title
          >
          <v-card-text class="pt-0">
            <div v-if="activeRanch" class="text-body-2">
              <div class="font-weight-medium">
                {{ activeRanch.DatasetName }}
              </div>
              <div class="text-caption grey--text">
                Owner: {{ activeRanch.DatasetOwner || "Unknown" }}
              </div>
              <div class="text-caption grey--text">
                Created:
                {{ formatDate(activeRanch.CreatedAt) || "Just now" }}
              </div>
              <div class="text-caption grey--text">
                Last updated:
                {{ formatDate(activeRanch.UpdatedAt) }}
              </div>
            </div>
            <div v-else class="text-caption grey--text">
              No ranch loaded yet.
            </div>
          </v-card-text>
          <v-card-actions>
            <!-- <v-btn
              v-if="activeRanch.DatasetOwner === currentUserEmail"
              text
              color="primary"
              :disabled="!activeRanch"
              @click="openRepairDialog"
            >
              <v-icon left small>mdi-wrench</v-icon>
              Repair
            </v-btn> -->
            <v-spacer></v-spacer>
            <v-btn
              text
              color="warning"
              :disabled="!activeRanch"
              @click="unloadRanch"
            >
              Unload Ranch
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <!-- Ranch lists -->
      <v-col cols="12" :md="activeRanch ? 8 : 12">
        <v-tabs v-model="activeTab" background-color="transparent">
          <v-tab key="mine">My Ranches</v-tab>
          <v-tab key="shared">Shared With Me</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- My ranches -->
          <v-tab-item key="mine">
            <v-card flat>
              <v-card-text class="pt-2">
                <v-data-table
                  :items="myRanches"
                  :headers="ownedHeaders"
                  item-key="id"
                  dense
                  disable-pagination
                  hide-default-footer
                  class="elevation-1 ranch-table"
                  @click:row="(_, row) => loadRanch(row.item)"
                >
                  <template v-slot:item.createdAt="{ item }">
                    {{ formatDate(item.createdAt) }}
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="primary"
                          @click.stop="openRanch(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon>mdi-open-in-new</v-icon>
                        </v-btn>
                      </template>
                      <span>Explore data</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="blue-grey darken-1"
                          @click.stop="openShareDialog(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon>mdi-share-variant</v-icon>
                        </v-btn>
                      </template>
                      <span>Share ranch</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="red darken-1"
                          @click.stop="confirmDelete(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Delete ranch</span>
                    </v-tooltip>
                  </template>
                </v-data-table>

                <div
                  v-if="!myRanches.length"
                  class="text-caption grey--text mt-3 text-center"
                >
                  You don't have any ranches yet. Use the Create Ranch button
                  above to get started.
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Shared ranches -->
          <v-tab-item key="shared">
            <v-card flat>
              <v-card-text class="pt-2">
                <v-data-table
                  :items="sharedRanches"
                  :headers="sharedHeaders"
                  item-key="id"
                  dense
                  disable-pagination
                  hide-default-footer
                  class="elevation-1 ranch-table"
                  @click:row="(_, row) => loadRanch(row.item)"
                >
                  <template v-slot:item.createdAt="{ item }">
                    {{ formatDate(item.createdAt) }}
                  </template>
                  <template v-slot:item.owner="{ item }">
                    {{ item.ownerEmail }}
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="primary"
                          @click.stop="openRanch(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon>mdi-open-in-new</v-icon>
                        </v-btn>
                      </template>
                      <span>Explore data</span>
                    </v-tooltip>
                  </template>
                </v-data-table>

                <div
                  v-if="!sharedRanches.length"
                  class="text-caption grey--text mt-3 text-center"
                >
                  No ranches have been shared with you yet.
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>

    <!-- Repair Ranch Dialog -->
    <v-dialog v-model="repairDialog.visible" max-width="600px">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-wrench</v-icon>
          Repair Issues with Ranch
        </v-card-title>

        <v-card-text class="pt-4">
          <div v-if="activeRanch" class="mb-4">
            <div class="text-subtitle-2 mb-1">
              Ranch: {{ activeRanch.DatasetName }}
            </div>
            <div class="text-caption grey--text">
              Owner: {{ activeRanch.DatasetOwner }}
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div class="text-subtitle-2 mb-3">Select repair operations:</div>

          <v-list dense>
            <v-list-item>
              <v-list-item-action>
                <v-checkbox
                  v-model="repairDialog.fixPermissions"
                  color="primary"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Fix Permissions</v-list-item-title>
                <v-list-item-subtitle
                  >If other users have incorrect access permissions to the
                  ranch</v-list-item-subtitle
                >
              </v-list-item-content>
            </v-list-item>

            <!-- Placeholder for future repair operations -->
            <!-- <v-list-item>
              <v-list-item-action>
                <v-checkbox
                  v-model="repairDialog.rebuildIndices"
                  color="primary"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Rebuild Indices</v-list-item-title>
                <v-list-item-subtitle
                  >Rebuild database indices for better performance</v-list-item-subtitle
                >
              </v-list-item-content>
            </v-list-item> -->
          </v-list>

          <v-alert v-if="repairDialog.error" type="error" dense class="mt-3">
            {{ repairDialog.error }}
          </v-alert>

          <v-alert
            v-if="repairDialog.success"
            type="success"
            dense
            class="mt-3"
          >
            {{ repairDialog.success }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeRepairDialog"
            :disabled="repairDialog.loading"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="executeRepair"
            :loading="repairDialog.loading"
            :disabled="!hasRepairOperationsSelected"
          >
            <v-icon left>mdi-wrench</v-icon>
            Run Repair
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create ranch dialog -->
    <v-dialog v-model="createDialog.visible" max-width="600px">
      <v-card>
        <v-card-title class="text-h6">Create a New Ranch</v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="newRanch.name"
            label="Ranch name"
            dense
            outlined
            hide-details="auto"
            autofocus
          />
          <div v-if="createError" class="error--text text-caption mt-2">
            {{ createError }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeCreateDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!canCreateRanch"
            @click="createRanch"
          >
            <v-icon left>mdi-plus</v-icon>
            Create Ranch
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteDialog.visible" max-width="600px" scrollable>
      <v-card>
        <v-card-title class="text-h6">
          Delete ranch: {{ deleteDialog.ranch?.name }}?
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            v-if="deleteDialog.loading"
            indeterminate
            color="primary"
            class="mb-3"
          ></v-progress-linear>

          <div v-if="deleteDialog.error" class="error--text text-body-2 mb-3">
            {{ deleteDialog.error }}
          </div>

          <div v-if="deleteDialog.checkData">
            <!-- Blocking reasons -->
            <v-alert
              v-if="deleteDialog.checkData.BlockingReasons?.length > 0"
              type="error"
              dense
              text
              class="mb-3"
            >
              <div class="font-weight-medium mb-1">Cannot delete ranch:</div>
              <ul class="pl-4 mb-0">
                <li
                  v-for="(reason, idx) in deleteDialog.checkData
                    .BlockingReasons"
                  :key="idx"
                  class="text-caption"
                >
                  {{ reason }}
                </li>
              </ul>
            </v-alert>

            <!-- Warnings -->
            <v-alert
              v-if="deleteDialog.checkData.DeletionWarnings?.length > 0"
              type="warning"
              dense
              text
              class="mb-3"
            >
              <div class="font-weight-medium mb-1">Warning:</div>
              <ul class="pl-4 mb-0">
                <li
                  v-for="(warning, idx) in deleteDialog.checkData
                    .DeletionWarnings"
                  :key="idx"
                  class="text-caption"
                >
                  {{ warning }}
                </li>
              </ul>
            </v-alert>

            <!-- Summary -->
            <div class="text-body-2 mb-3">
              <strong>This will delete:</strong>
              <ul class="pl-4 mt-1">
                <li>{{ deleteDialog.checkData.TotalDatasets }} dataset(s)</li>
                <li>{{ deleteDialog.checkData.TotalFiles }} paddock file(s)</li>
              </ul>
            </div>

            <!-- Dataset details -->
            <v-expansion-panels
              v-if="deleteDialog.checkData.Datasets?.length > 0"
              flat
            >
              <v-expansion-panel>
                <v-expansion-panel-header
                  class="text-caption font-weight-medium"
                >
                  View dataset details ({{
                    deleteDialog.checkData.Datasets.length
                  }})
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left text-caption">Dataset</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(ds, idx) in deleteDialog.checkData.Datasets"
                          :key="idx"
                        >
                          <td class="text-caption">
                            {{ ds.DatasetName }}
                            <v-chip
                              v-if="ds.HasSeasonData"
                              x-small
                              color="orange"
                              text-color="white"
                              class="ml-1"
                            >
                              Season
                            </v-chip>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <p class="text-body-2 mt-3 mb-0">
              <strong>This action cannot be undone.</strong> Are you sure you
              want to proceed?
            </p>
          </div>

          <p v-else class="text-body-2 mb-0">Checking ranch data...</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="deleteDialog.visible = false"
            :disabled="deleteDialog.loading"
          >
            Cancel
          </v-btn>
          <v-btn
            color="red darken-1"
            dark
            @click="deleteRanch"
            :disabled="
              deleteDialog.loading || !deleteDialog.checkData?.CanDelete
            "
            :loading="deleteDialog.loading"
          >
            Delete Ranch
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Share dialog -->
    <v-dialog v-model="shareDialog.visible" max-width="520px">
      <v-card>
        <v-card-title class="text-h6">
          Share ranch: {{ shareDialog.ranch?.name }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="shareDialog.email"
            label="User email to share with"
            dense
            outlined
            hide-details="auto"
          />
          <v-select
            v-model="shareDialog.permission"
            :items="permissionOptions"
            item-text="label"
            item-value="value"
            label="Permission level"
            dense
            outlined
            hide-details="auto"
            class="mt-2"
          />

          <div v-if="shareError" class="error--text text-caption mt-2">
            {{ shareError }}
          </div>

          <div v-if="shareDialog.ranch" class="mt-4">
            <div class="text-caption mb-1 font-weight-medium">
              Current shared access
            </div>
            <v-chip
              v-for="(entry, index) in shareDialog.ranch.sharedWith"
              :key="index"
              x-small
              class="ma-1"
              color="blue-grey lighten-4"
              @click="
                shareDialog.email = entry.email;
                shareDialog.permission = entry.permission;
              "
              :disabled="shareDialog.loading"
            >
              {{ entry.email }} ({{ entry.permission }})
            </v-chip>
            <div
              v-if="!shareDialog.ranch.sharedWith.length"
              class="text-caption grey--text mt-1"
            >
              Not currently shared with anyone.
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="warning"
            text
            @click="closeShareDialog"
            :disabled="shareDialog.loading"
            >Close</v-btn
          >
          <v-btn
            color="primary"
            @click="applyShare"
            :disabled="shareDialog.loading"
            :loading="shareDialog.loading"
            >Apply</v-btn
          >
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DataShareUserList from "@/components/data/DataShareUserList.vue";
/* eslint-disable */
import { shareRanch, checkRanchDelete, fixRanchPermissions } from "@/api/v2.js";
import { getDataset, deleteDataset } from "@/api/v2.js";

export default {
  name: "MyRanches",

  components: { DataShareUserList },

  data() {
    return {
      activeTab: 0,
      activeRanch: null,
      refreshing: false,
      newRanch: {
        name: "",
      },
      createError: null,
      shareError: null,
      createDialog: {
        visible: false,
      },
      deleteDialog: {
        visible: false,
        ranch: null,
        loading: false,
        checkData: null,
        error: null,
      },
      shareDialog: {
        visible: false,
        ranch: null,
        email: "",
        permission: "edit",
        loading: false,
      },
      repairDialog: {
        visible: false,
        loading: false,
        error: null,
        success: null,
        fixPermissions: true,
        // Add more repair operations here as needed
        // rebuildIndices: false,
      },
      permissionOptions: [
        { label: "Edit", value: "edit" },
        // { label: "View only", value: "view" },
        { label: "None", value: "none" },
      ],
      tagFilter: "Paddock Boundaries",
      requestingPermissions: [],
      datasetCache: {},
    };
  },

  computed: {
    currentUserEmail() {
      const user = this.$store.state.account.user;
      return user?.attributes?.email;
    },

    hasRepairOperationsSelected() {
      return this.repairDialog.fixPermissions;
      // Add more operations when implemented:
      // return this.repairDialog.fixPermissions || this.repairDialog.rebuildIndices;
    },

    userDatasetList() {
      return this.$store.state.accountData.datasetList.datasets.AllDatasets.filter(
        (dataset) => dataset.Tags.includes(this.tagFilter),
      ).sort((a, b) => a.DatasetName.localeCompare(b.DatasetName));
    },

    // All ranch datasets decorated with owner and permissions parsed from Tags
    ranchItems() {
      return this.userDatasetList.map((dataset) => {
        const permissions = this.parsePermissions(dataset.Tags);
        const currentUserPermission = permissions.find(
          (p) => p.email.toLowerCase() === this.currentUserEmail.toLowerCase(),
        )?.permission;

        return {
          id: `${dataset.DatasetName}|${dataset.DatasetOwner}`,
          name: dataset.DatasetName,
          ownerEmail: dataset.DatasetOwner,
          createdAt: dataset.CreatedAt,
          lastOpenedAt: null,
          sharedWith: permissions,
          currentUserPermission,
          dataset,
        };
      });
    },

    myRanches() {
      return this.ranchItems.filter(
        (item) => item.ownerEmail === this.currentUserEmail,
      );
    },

    sharedRanches() {
      return this.ranchItems.filter(
        (item) => item.ownerEmail !== this.currentUserEmail,
      );
    },

    canCreateRanch() {
      return this.newRanch.name.trim().length > 0;
    },

    ownedHeaders() {
      return [
        { text: "Name", value: "name" },
        { text: "Created", value: "createdAt" },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ];
    },

    sharedHeaders() {
      return [
        { text: "Name", value: "name" },
        { text: "Owner", value: "owner" },
        { text: "Created", value: "createdAt" },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ];
    },

    // Data passed into the sharing component; for ranches we only ever
    // operate on a single ranch at a time.
    selectedData() {
      return this.shareDialog.ranch ? [this.shareDialog.ranch] : [];
    },

    // Map ranch.sharedWith entries (email + permission) into the shape
    currentSharedPermissions() {
      const ranch = this.shareDialog.ranch;
      if (!ranch || !Array.isArray(ranch.sharedWith)) return [];

      return ranch.sharedWith.map((entry) => ({
        user: entry.email,
        view: true,
        edit: entry.permission === "edit",
      }));
    },
  },

  methods: {
    // Parse dataset.Tags entries like "EDIT:user@example.com" or "VIEW:user@example.com"
    parsePermissions(tags) {
      if (!Array.isArray(tags)) {
        console.warn("Invalid tags for permission parsing:", tags);
        return [];
      }

      return tags
        .filter(
          (tag) =>
            tag.startsWith("EDIT:") && !tag.includes(this.currentUserEmail),
        )
        .map((tag) => {
          const [rawPermission, email] = tag.split(":");
          const permission = rawPermission === "EDIT" ? "edit" : "view";

          return { email, permission };
        });
    },

    formatDate(value) {
      if (!value) return null;
      const date = typeof value === "string" ? new Date(value) : value;
      return date.toLocaleString();
    },

    openRepairDialog() {
      if (!this.activeRanch) return;
      this.repairDialog.visible = true;
      this.repairDialog.error = null;
      this.repairDialog.success = null;
      this.repairDialog.fixPermissions = true;
    },

    closeRepairDialog() {
      this.repairDialog.visible = false;
      this.repairDialog.error = null;
      this.repairDialog.success = null;
      this.repairDialog.loading = false;
    },

    async executeRepair() {
      if (!this.activeRanch) return;

      this.repairDialog.loading = true;
      this.repairDialog.error = null;
      this.repairDialog.success = null;

      const operations = [];
      const results = [];

      try {
        // Fix Permissions
        if (this.repairDialog.fixPermissions) {
          operations.push("Fix Permissions");
          try {
            await fixRanchPermissions(
              this.activeRanch.DatasetName,
              this.activeRanch.DatasetOwner,
            );
            results.push("✓ Fixed permissions");
          } catch (error) {
            console.error("Failed to fix permissions:", error);
            throw new Error(
              `Fix permissions failed: ${error.message || error}`,
            );
          }
        }

        // Add more repair operations here as they're implemented
        // if (this.repairDialog.rebuildIndices) {
        //   operations.push('Rebuild Indices');
        //   await rebuildRanchIndices(...);
        //   results.push('✓ Rebuilt indices');
        // }

        // Success
        this.repairDialog.success = results.join("\n");
        this.$showAlert({
          text: `Ranch repair completed successfully: ${operations.join(", ")}`,
          type: "success",
        });

        // Optionally refresh ranch data after repair
        await this.refreshRanchList();

        // Close dialog after a short delay to show success message
        setTimeout(() => {
          if (this.repairDialog.visible) {
            this.closeRepairDialog();
          }
        }, 2000);
      } catch (error) {
        console.error("Ranch repair failed:", error);
        this.repairDialog.error =
          error.message || "An error occurred during repair";
        this.$showAlert({
          text: `Ranch repair failed: ${error.message || error}`,
          type: "error",
        });
      } finally {
        this.repairDialog.loading = false;
      }
    },

    openRepairDialog() {
      if (!this.activeRanch) return;
      this.repairDialog.visible = true;
      this.repairDialog.error = null;
      this.repairDialog.success = null;
      this.repairDialog.fixPermissions = true;
    },

    closeRepairDialog() {
      this.repairDialog.visible = false;
      this.repairDialog.error = null;
      this.repairDialog.success = null;
      this.repairDialog.loading = false;
    },

    async executeRepair() {
      if (!this.activeRanch) return;

      this.repairDialog.loading = true;
      this.repairDialog.error = null;
      this.repairDialog.success = null;

      const operations = [];
      const results = [];

      try {
        // Fix Permissions
        if (this.repairDialog.fixPermissions) {
          operations.push("Fix Permissions");
          try {
            await fixRanchPermissions(
              this.activeRanch.DatasetName,
              this.activeRanch.DatasetOwner,
            );
            results.push("✓ Fixed permissions");
          } catch (error) {
            console.error("Failed to fix permissions:", error);
            throw new Error(
              `Fix permissions failed: ${error.message || error}`,
            );
          }
        }

        // Add more repair operations here as they're implemented
        // if (this.repairDialog.rebuildIndices) {
        //   operations.push('Rebuild Indices');
        //   await rebuildRanchIndices(...);
        //   results.push('✓ Rebuilt indices');
        // }

        // Success
        this.repairDialog.success = results.join("\n");
        this.$showAlert({
          text: `Ranch repair completed successfully: ${operations.join(", ")}`,
          type: "success",
        });

        // Optionally refresh ranch data after repair
        await this.refreshRanchList();

        // Close dialog after a short delay to show success message
        setTimeout(() => {
          if (this.repairDialog.visible) {
            this.closeRepairDialog();
          }
        }, 2000);
      } catch (error) {
        console.error("Ranch repair failed:", error);
        this.repairDialog.error =
          error.message || "An error occurred during repair";
        this.$showAlert({
          text: `Ranch repair failed: ${error.message || error}`,
          type: "error",
        });
      } finally {
        this.repairDialog.loading = false;
      }
    },

    async refreshRanchList() {
      this.refreshing = true;
      try {
        await this.$store.dispatch("accountData/refreshDatasetList");
        this.$showAlert({
          text: "Ranch list refreshed successfully.",
          type: "success",
        });
      } catch (error) {
        this.$showAlert({
          text: `Failed to refresh ranch list: ${error.message || error}`,
          type: "error",
        });
      } finally {
        this.refreshing = false;
      }
    },

    openCreateDialog() {
      this.createDialog.visible = true;
      this.createError = null;
    },

    closeCreateDialog() {
      this.createDialog.visible = false;
      this.newRanch.name = "";
      this.createError = null;
    },

    async createRanch() {
      this.createError = null;
      const name = this.newRanch.name.trim();
      if (!name) {
        this.createError = "Ranch name is required.";
        return;
      }

      const duplicate = this.ranchItems.some(
        (r) => r.name.toLowerCase() === name.toLowerCase(),
      );
      if (duplicate) {
        this.createError = "A ranch with this name already exists.";
        return;
      }

      // Unload any currently loaded ranch
      if (this.activeRanch) {
        await this.unloadRanch();
      }

      // Use the existing Season Manager flow to create a new ranch
      this.$router.push({
        name: "Season Manager",
        params: {
          newRanchName: name,
        },
      });

      // Close dialog and reset form
      this.closeCreateDialog();
    },

    async confirmDelete(ranch) {
      this.deleteDialog.ranch = ranch;
      this.deleteDialog.visible = true;
      this.deleteDialog.loading = true;
      this.deleteDialog.checkData = null;
      this.deleteDialog.error = null;

      try {
        const checkData = await checkRanchDelete(ranch.name, ranch.ownerEmail);
        this.deleteDialog.checkData = checkData;
      } catch (error) {
        console.error("Failed to check ranch delete:", error);
        this.deleteDialog.error =
          error.message || "Failed to check ranch status";
      } finally {
        this.deleteDialog.loading = false;
      }
    },

    async deleteRanch() {
      const ranch = this.deleteDialog.ranch;
      if (!ranch || !this.deleteDialog.checkData?.CanDelete) return;

      this.deleteDialog.loading = true;
      this.deleteDialog.error = null;

      try {
        // Delete all datasets in the ranch
        const datasets = this.deleteDialog.checkData.Datasets || [];

        // Add the main ranch dataset if it's not in the list
        const mainDatasetInList = datasets.some(
          (ds) =>
            ds.DatasetName === ranch.name &&
            ds.DatasetOwner === ranch.ownerEmail,
        );

        const datasetsToDelete = [...datasets];
        if (!mainDatasetInList) {
          datasetsToDelete.push({
            DatasetName: ranch.name,
            DatasetOwner: ranch.ownerEmail,
          });
        }

        // Delete all datasets
        for (const ds of datasetsToDelete) {
          await deleteDataset(ds.DatasetName, ds.DatasetOwner);
        }

        // Clear active ranch if it was deleted
        if (this.activeRanch && this.activeRanch.DatasetName === ranch.name) {
          await this.unloadRanch();
        }

        // Refresh dataset list
        await this.$store.dispatch("accountData/refreshDatasetList");

        this.$showAlert({
          text: `Ranch "${ranch.name}" has been deleted successfully.`,
          type: "success",
        });

        this.deleteDialog.visible = false;
      } catch (error) {
        console.error("Failed to delete ranch:", error);
        this.deleteDialog.error = error.message || "Failed to delete ranch";
      } finally {
        this.deleteDialog.loading = false;
        this.deleteDialog.ranch = null;
        this.deleteDialog.checkData = null;
      }
    },

    async loadRanch(ranch) {
      if (!ranch) return;

      // Unload any currently loaded ranch first
      if (this.activeRanch) {
        await this.unloadRanch();
      }

      // fetch main dataset file
      const fetchedRanch = await this.fetchDataset(ranch.dataset);

      this.$store.commit("mapping/setSingleDataset", fetchedRanch);
      this.$store.commit("settings/updateSubAppName", fetchedRanch.DatasetName);

      try {
        const result = await this.$store.dispatch(
          "accountData/refreshSeasonList",
          {
            datasetName: fetchedRanch.DatasetName,
            datasetOwner: fetchedRanch.DatasetOwner,
          },
        );
      } catch (error) {
        this.$showAlert({
          text: `Failed to load grazing seasons for ranch "${fetchedRanch.DatasetName}": ${error.message}\n\nThis ranch may be broken. If this ranch is not needed, would you like to delete it? If this ranch is still needed or contains important data, please contact support for assistance.`,
          type: "error",
          choice: true,
        }).then((confirmed) => {
          if (confirmed) {
            this.confirmDelete(ranch);
          }
        });
        return;
      }

      fetchedRanch.lastOpenedAt = new Date();

      this.activeRanch = { ...fetchedRanch };
    },

    async unloadRanch() {
      this.$store.commit("mapping/clearSingleDataset");
      this.$store.commit("mapping/removeAllLayers");
      this.$store.commit("settings/updateSubAppName", null);
      this.$store.commit("accountData/clearSeasonList");
      this.activeRanch = null;
    },

    async openRanch(ranch) {
      if (!ranch) return;

      // loadRanch first.
      await this.loadRanch(ranch);

      this.$router.push({
        name: "Data Viewer",
        query: {
          datasetName: ranch.dataset.DatasetName,
          datasetOwner: ranch.dataset.DatasetOwner,
        },
      });
    },

    async openShareDialog(ranch) {
      if (!ranch) return;
      this.shareError = null;
      this.shareDialog.email = "";
      this.shareDialog.permission = "view";
      this.shareDialog.loading = true;

      try {
        // Check cache first
        const cacheKey = `${ranch.name}|${ranch.ownerEmail}`;
        let fullDataset = this.datasetCache[cacheKey];

        if (!fullDataset) {
          // Lazy load the full dataset if not cached
          fullDataset = await this.fetchDataset(ranch.dataset);
          if (fullDataset) {
            this.datasetCache[cacheKey] = fullDataset;
          }
        }

        // Create an enhanced ranch object with the full dataset
        const enhancedRanch = {
          ...ranch,
          fullDataset,
        };

        this.shareDialog.ranch = enhancedRanch;
        this.shareDialog.visible = true;
      } catch (error) {
        this.shareError = error?.message || "Failed to load ranch details.";
      } finally {
        this.shareDialog.loading = false;
      }
    },

    closeShareDialog() {
      this.shareDialog.visible = false;
      this.shareDialog.ranch = null;
      this.shareDialog.email = "";
      this.shareDialog.permission = "view";
      this.shareDialog.loading = false;
      this.shareError = null;
    },

    async applyShare() {
      this.shareDialog.loading = true;
      const email = this.shareDialog.email.trim();
      if (!email) {
        this.shareError = "Please enter a user email to share with.";
        this.shareDialog.loading = false;
        return;
      }

      await this.modifyPermission(email, this.shareDialog.permission);
      this.shareDialog.loading = false;
    },

    async fetchDataset(selectedDataset) {
      try {
        const dataset = await getDataset(
          selectedDataset.DatasetName,
          selectedDataset.DatasetOwner,
        );

        if (dataset.Errors) {
          throw new Error(dataset.Errors);
        }
        if (dataset.Warnings) {
          this.$showAlert({
            text: dataset.Warnings,
            type: "warning",
          });
        }

        return dataset;
      } catch (error) {
        this.$showAlert({
          text: error,
          type: "error",
        });
      }
    },

    // Called when a permission checkbox is turned on in DataShareUserList.
    // For ranches, this maps directly to a single /ranch/share API call
    // followed by a dataset-list refresh so tags and permissions stay in sync.
    async modifyPermission(userEmail, permission) {
      const ranch = this.shareDialog.ranch;
      if (!ranch) return;

      this.shareError = null;
      this.requestingPermissions.push(userEmail);

      try {
        const permissionToUse = permission === "edit" ? "edit" : "none";

        await shareRanch(
          ranch.name,
          ranch.ownerEmail,
          userEmail,
          permissionToUse,
        );

        // Clear the cache for this ranch so next time it's fetched fresh
        const cacheKey = `${ranch.name}|${ranch.ownerEmail}`;
        delete this.datasetCache[cacheKey];

        // Refresh the dataset list so ranchItems and permissions reflect
        // the latest tags from the backend.
        await this.$store.dispatch("accountData/refreshDatasetList");

        // Re-sync the active ranch reference from the updated list.
        const updated = this.ranchItems.find(
          (item) =>
            item.name === ranch.name && item.ownerEmail === ranch.ownerEmail,
        );
        if (updated) {
          // Re-fetch the full dataset to get the latest tags
          const fullDataset = await this.fetchDataset(updated.dataset);
          const enhancedRanch = {
            ...updated,
            fullDataset,
          };
          this.datasetCache[cacheKey] = fullDataset;
          this.shareDialog.ranch = enhancedRanch;
        }
      } catch (error) {
        this.shareError =
          (error && error.message) ||
          "Failed to update ranch sharing permissions.";
        if (error && error.response && error.response.data) {
          const { Errors, Warnings } = error.response.data;
          if (Errors || Warnings) {
            this.$showAlert({
              text: Errors || Warnings,
              type: Errors ? "error" : "warning",
            });
          }
        }
      } finally {
        this.requestingPermissions = this.requestingPermissions.filter(
          (u) => u !== userEmail,
        );
      }
    },

    // For now, ranch sharing supports downgrading from edit -> view, but not
    // fully removing all access. If the user unchecks "edit", we switch to
    // view-only. If they attempt to uncheck "view", we immediately revert
    // the change.
    async removePermission(userPermission) {
      const ranch = this.shareDialog.ranch;
      if (!ranch) return;

      // Prevent completely removing view access via this UI for now.
      if (checkboxModified === "view") {
        // Revert the checkbox change locally.
        userPermission.view = true;
        return;
      }

      // checkboxModified === "edit" here: downgrade to view-only.
      this.shareError = null;
      this.requestingPermissions.push(userPermission.user);

      try {
        await shareRanch(
          ranch.name,
          ranch.ownerEmail,
          userPermission.user,
          "view",
        );

        // Clear the cache for this ranch so next time it's fetched fresh
        const cacheKey = `${ranch.name}|${ranch.ownerEmail}`;
        delete this.datasetCache[cacheKey];

        await this.$store.dispatch("accountData/refreshDatasetList");

        const updated = this.ranchItems.find(
          (item) =>
            item.name === ranch.name && item.ownerEmail === ranch.ownerEmail,
        );
        if (updated) {
          // Re-fetch the full dataset to get the latest tags
          const fullDataset = await this.fetchDataset(updated.dataset);
          const enhancedRanch = {
            ...updated,
            fullDataset,
          };
          this.datasetCache[cacheKey] = fullDataset;
          this.shareDialog.ranch = enhancedRanch;
        }
      } catch (error) {
        this.shareError =
          (error && error.message) ||
          "Failed to update ranch sharing permissions.";
        if (error && error.response && error.response.data) {
          const { Errors, Warnings } = error.response.data;
          if (Errors || Warnings) {
            this.$showAlert({
              text: Errors || Warnings,
              type: Errors ? "error" : "warning",
            });
          }
        }
      } finally {
        this.requestingPermissions = this.requestingPermissions.filter(
          (u) => u !== userPermission.user,
        );
      }
    },
  },

  created() {
    // Ensure dataset list is loaded so userDatasetList/ranchItems are populated
    const datasetListState = this.$store.state.accountData.datasetList;
    if (!datasetListState.lastUpdated && !datasetListState.fetchingUpdates) {
      this.$store.dispatch("accountData/refreshDatasetList");
    }
  },

  mounted() {
    // set active ranch if one is loaded in the store
    const activeRanchInStore = this.$store.state.mapping.singleDataset;

    if (activeRanchInStore) {
      // Search for the matching ranch in ranchItems
      const matchingRanch = this.ranchItems.find(
        (ranch) =>
          ranch.name === activeRanchInStore.DatasetName &&
          ranch.ownerEmail === activeRanchInStore.DatasetOwner,
      );

      if (matchingRanch) {
        // Use the full ranch object from ranchItems which has all properties properly set
        this.activeRanch = {
          ...matchingRanch,
          ...activeRanchInStore,
        };
      } else {
        // Fallback: construct from store data if not found in ranchItems
        this.activeRanch = activeRanchInStore;
      }
    }
  },
};
</script>

<style scoped>
.my-ranches {
  padding-top: 16px;
}

.summary {
  line-height: 1.3;
}

.ranch-table >>> tbody tr {
  cursor: pointer;
}

.text-md-right {
  text-align: right;
}

@media (max-width: 959px) {
  .text-md-right {
    text-align: left;
  }
}
</style>
